import { PricingPlanBenefitsServer } from '@wix/ambassador-pricing-plan-benefits-server/http';
import {
  Balance,
  GetBalanceRequest,
} from '@wix/ambassador-pricing-plan-benefits-server/types';
import { queryServices } from '@wix/ambassador-bookings-services-v2-service/http';
import {
  BulkRequest,
  BulkResponse,
  GetActiveFeaturesResponse,
  ServicesCatalogServer,
} from '@wix/ambassador-services-catalog-server/http';
import { queryServiceOptionsAndVariants } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/http';
import { ServiceOptionsAndVariants } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import {
  mapCatalogResourceResponseToStaffMember,
  mapCatalogServiceResponseToService,
  mapResponseToBusinessInfo,
} from '@wix/bookings-uou-mappers';
import { BusinessInfoBase, ServiceType } from '@wix/bookings-uou-types';
import {
  CalculateMultiSlotAvailabilityRequest,
  QueryAvailabilityRequest,
  QueryAvailabilityResponse,
  Slot,
} from '@wix/ambassador-availability-calendar/types';
import type { V2Slot } from '@wix/ambassador-bookings-v2-booking/types';
import { AvailabilityCalendar } from '@wix/ambassador-availability-calendar/http';
import { Experiments, IHttpClient } from '@wix/yoshi-flow-editor';
import {
  Booking,
  CheckoutApiConfig,
  createConfig,
  getBooking,
  reschedule,
} from '@wix/bookings-checkout-api';
import { CalendarCatalogData } from './types';
import { BULK_REQUEST_LIMIT } from '../constants/constants';
import { getSiteRolloutStatusForV1Shutdown } from '@wix/ambassador-bookings-v1-site-rollout-status/http';

interface CatalogDataFilter {
  servicesOptions?: {
    slug?: string;
    include: boolean;
    businessLocations?: string[];
    categories?: string[];
    id?: string[];
    isBookOnlineAllowed?: boolean;
    tags?: ServiceType[];
  };
  resourcesOptions?: {
    slug?: string;
    include: boolean;
  };
  locationsOptions?: {
    businessLocations?: string[];
    include: boolean;
  };
}

export const CATALOG_SERVER_URL = '_api/services-catalog';
export const AVAILABILITY_SERVER_URL = '_api/availability-calendar';
export const CHECKOUT_SERVER_URL = '_api/checkout-server';
export const PRICING_PLAN__BENEFITS_SERVER_API = '_api/pricing-plan-benefits';
export const SERVICE_OPTIONS_AND_VARIANTS_SERVER_API =
  '_api/service-options-and-variants';

export class BookingsApi {
  private authorization: string;
  private experiments: Experiments;
  private bookingsCheckoutApiConfig: CheckoutApiConfig;
  private httpClient: IHttpClient;
  private catalogServer: ReturnType<typeof ServicesCatalogServer>;
  private availabilityCalendarServer: ReturnType<typeof AvailabilityCalendar>;
  private pricingPlanBenefitsServer: ReturnType<
    typeof PricingPlanBenefitsServer
  >;

  constructor({
    authorization,
    baseUrl,
    experiments,
    httpClient,
  }: {
    authorization: string;
    baseUrl: string;
    experiments: Experiments;
    httpClient: IHttpClient;
  }) {
    this.authorization = authorization;
    this.experiments = experiments;
    this.bookingsCheckoutApiConfig = createConfig({
      getAuthorization: () => authorization,
      getBaseUrl: () => baseUrl,
      experiments: experiments as any,
      httpClient: httpClient as any,
    });
    this.httpClient = httpClient;

    this.catalogServer = ServicesCatalogServer(
      `${baseUrl}${CATALOG_SERVER_URL}`,
    );
    this.availabilityCalendarServer = AvailabilityCalendar(
      `${baseUrl}${AVAILABILITY_SERVER_URL}`,
    );
    this.pricingPlanBenefitsServer = PricingPlanBenefitsServer(
      `${baseUrl}${PRICING_PLAN__BENEFITS_SERVER_API}`,
    );
  }

  async getCatalogData({
    servicesOptions,
    resourcesOptions,
    locationsOptions,
  }: CatalogDataFilter): Promise<CalendarCatalogData> {
    const servicesCatalogService = this.catalogServer.Bulk();

    const bulkRequest: BulkRequest = this.createBulkRequest({
      servicesOptions,
      resourcesOptions,
      locationsOptions,
    });

    const catalogData: BulkResponse = await servicesCatalogService({
      Authorization: this.authorization,
    }).get(bulkRequest);

    const services = servicesOptions?.include
      ? catalogData.responseServices!.services!.map((service) =>
          mapCatalogServiceResponseToService(service),
        )
      : [];
    const businessInfo: BusinessInfoBase = mapResponseToBusinessInfo(
      catalogData.responseBusiness!,
    );
    const activeFeatures: GetActiveFeaturesResponse =
      catalogData.responseBusiness!.activeFeatures!;

    const staffMembers = catalogData.responseResources?.resources?.map(
      mapCatalogResourceResponseToStaffMember,
    );

    return {
      services,
      businessInfo,
      activeFeatures,
      staffMembers,
      seoData: catalogData.responseServices?.services || [],
    };
  }

  async getServiceVariants(
    serviceIds: string[],
  ): Promise<ServiceOptionsAndVariants[] | undefined> {
    try {
      const {
        data: { serviceOptionsAndVariantsList },
      } = await this.httpClient.request(
        queryServiceOptionsAndVariants({
          query: {
            filter: {
              serviceId: {
                $in: serviceIds,
              },
            },
          },
        }),
      );
      return serviceOptionsAndVariantsList;
    } catch (e) {
      console.log(e);
      return undefined;
    }
  }

  async getSlotsAvailability(
    queryAvailabilityRequest: QueryAvailabilityRequest,
  ): Promise<QueryAvailabilityResponse> {
    const availabilityCalendarService =
      this.availabilityCalendarServer.AvailabilityCalendar();

    const availabilityCalendarAPIs = availabilityCalendarService({
      Authorization: this.authorization,
      'X-Time-Budget': 30000,
    });
    if (isMultiSlotsAvailabilityRequestType(queryAvailabilityRequest)) {
      const multiSlotsAvailabilityResponse =
        await availabilityCalendarAPIs.calculateMultiSlotAvailability(
          queryAvailabilityRequest as CalculateMultiSlotAvailabilityRequest,
        );
      return {
        availabilityEntries: multiSlotsAvailabilityResponse.slots,
        pagingMetadata: {
          ...multiSlotsAvailabilityResponse.cursorPagingMetadata,
        },
      };
    }
    return availabilityCalendarAPIs.queryAvailability(queryAvailabilityRequest);
  }

  async getBookingDetails(bookingId: string) {
    return getBooking({ bookingId }, this.bookingsCheckoutApiConfig);
  }

  async rescheduleBooking({ booking, slot }: { booking: Booking; slot: Slot }) {
    const slotV2 = slot as V2Slot;
    return reschedule(
      { booking, slot: slotV2 },
      this.bookingsCheckoutApiConfig,
    );
  }

  private createBulkRequest({
    servicesOptions,
    resourcesOptions,
  }: CatalogDataFilter): BulkRequest {
    const nonEmptyCategoryFilter = {
      'category.id': {
        $contains: '-',
      },
    };

    return {
      requestServices: servicesOptions?.include
        ? {
            includeDeleted: false,
            query: {
              fieldsets: [],
              filter: JSON.stringify({
                'slugs.name': servicesOptions.slug
                  ? servicesOptions.slug
                  : undefined,
                ...(servicesOptions.id
                  ? { 'service.id': { $in: servicesOptions.id } }
                  : {}),
                'service.customProperties.uouHidden': 'false',
                ...(servicesOptions?.businessLocations
                  ? {
                      'service.schedules.availability.locations.businessLocation.ids':
                        servicesOptions?.businessLocations,
                    }
                  : {}),
                ...(servicesOptions.categories
                  ? {
                      $and: [
                        {
                          'category.id': {
                            $in: servicesOptions.categories,
                          },
                        },
                        nonEmptyCategoryFilter,
                      ],
                    }
                  : nonEmptyCategoryFilter),
                'schedules.tags': servicesOptions.tags
                  ? { $in: servicesOptions.tags }
                  : undefined,
                'service.policy.isBookOnlineAllowed':
                  servicesOptions.isBookOnlineAllowed,
              }),
              paging: {
                limit: 500,
              },
              fields: [],
              sort: [],
            },
          }
        : undefined,
      requestBusiness: {
        suppressNotFoundError: false,
      },
      requestListResources: resourcesOptions?.include
        ? {
            query: {
              fieldsets: [],
              filter: resourcesOptions.slug
                ? `{"slugs.name": "${resourcesOptions.slug}"}`
                : null,
              paging: {
                limit: BULK_REQUEST_LIMIT,
              },
              fields: [],
              sort: [],
            },
          }
        : undefined,
    };
  }

  async getPurchasedPricingPlans({
    contactId,
    authorization,
  }: {
    contactId: string;
    authorization: string;
  }): Promise<Balance[]> {
    const balanceRequest: GetBalanceRequest = {
      contactId,
    };
    const benefits = await this.pricingPlanBenefitsServer
      .MemberBenefits()({ Authorization: authorization })
      .getBalance(balanceRequest);
    return benefits.balanceItems || [];
  }

  async isEcomSite(): Promise<boolean> {
    try {
      const { data: response } = await this.httpClient.request(
        getSiteRolloutStatusForV1Shutdown({}),
      );
      return Boolean(response.siteRolloutStatus?.isBookingPlatformReady);
    } catch (e) {
      return true;
    }
  }

  async queryServicesV2({ serviceIds }: { serviceIds: string[] }) {
    const filter: Record<string, any> = {
      'onlineBooking.enabled': true,
      $or: [{ hidden: false }, { hidden: { $exists: false } }],
      id: { $in: serviceIds },
    };

    const { data } = await this.httpClient.request(
      queryServices({
        query: {
          filter,
        },
      }),
    );

    return data;
  }
}

const isMultiSlotsAvailabilityRequestType = (
  availabilityRequest:
    | QueryAvailabilityRequest
    | CalculateMultiSlotAvailabilityRequest,
): availabilityRequest is CalculateMultiSlotAvailabilityRequest => {
  return (
    (availabilityRequest as CalculateMultiSlotAvailabilityRequest).slots !==
    undefined
  );
};
